import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import ReviewsCarousel from '../components/ReviewsCarousel';
import InstagramReels from '../components/InstagramReels';
import { useProducts, useBestsellers, useNewArrivals, useCategoryTree, useBanners, useClients } from '../hooks/useQueries';
import { ReactGoogleReviews } from "react-google-reviews";
import "react-google-reviews/dist/index.css";
import './Home.css';

const testimonials = [
  { id: 1, name: 'Priya S.', city: 'Mumbai', rating: 5, text: 'The packaging was absolutely stunning. My friend was blown away! Will definitely order again for every occasion.' },
  { id: 2, name: 'Arjun M.', city: 'Bangalore', rating: 5, text: 'Ordered a birthday hamper — it arrived on time and looked even better than the photos. Highly recommend!' },
  { id: 3, name: 'Ritu K.', city: 'Delhi', rating: 5, text: 'The Diwali gifting collection is gorgeous. My clients loved every single gift. Fcreation is my go-to now.' },
];

/* ─── Category icon map (fallback emojis) ───────────────── */
const CAT_ICONS = {
  'personalised-gifts': { icon: '🎀', color: '#fde68a' },
  'mens-essentials': { icon: '👔', color: '#dbeafe' },
  'kids-special': { icon: '🧸', color: '#a7f3d0' },
  'corporate-gifts': { icon: '💼', color: '#fed7aa' },
  'womens-essentials': { icon: '👜', color: '#fbcfe8' },
};
const defaultCat = { icon: '🎁', color: '#e5e7eb' };

/* ─── Loading skeleton ──────────────────────────────────── */
const ProductSkeleton = () => (
  <div className="product-skeleton">
    <div className="skeleton-img" />
    <div className="skeleton-line" style={{ width: '60%' }} />
    <div className="skeleton-line" style={{ width: '80%' }} />
    <div className="skeleton-line" style={{ width: '40%' }} />
  </div>
);

/* ─── Section error state ───────────────────────────────── */
const SectionError = ({ message = 'Failed to load products. Make sure the API server is running.' }) => (
  <div className="section-error">
    <span>⚠️</span> {message}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   HOME PAGE
═══════════════════════════════════════════════════════════ */
const Home = () => {
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  /* TanStack Query hooks */
  const { data: banners = [], isLoading: bannersLoading } = useBanners();
  const { data: allProducts = [], isLoading: allLoading, isError: allError } = useProducts();
  const { data: bestsellers = [], isLoading: bsLoading, isError: bsError } = useBestsellers();
  const { data: newArrivals = [], isLoading: naLoading, isError: naError } = useNewArrivals();
  // Only top-level (primary) categories belong in "Shop by Occasion" — subcategories
  // are reachable via the category page itself, not this home-page grid.
  const { data: categoryTree = [], isLoading: catsLoading } = useCategoryTree();
  const { data: clients = [], isLoading: clientsLoading } = useClients();
  const featurableWidgetId = "ccc0f216-8f82-4858-899b-5d3f12f2a880"

  // Auto-advance hero — only when banners are loaded and there are multiple
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  /* Fallback: if no is_featured products, show first 8 */
  const featuredProducts = bestsellers.length ? bestsellers : allProducts.slice(0, 8);
  /* Fallback: if no is_new_arrival products, show next 8 */
  const newProducts = newArrivals.length ? newArrivals : allProducts.slice(0, 8);

  const handleBannerClick = (redirectUrl) => {
    if (!redirectUrl) return;
    try {
      // Parse out just the path + query, ignore the origin the API sends
      // (it may be localhost in dev, but a different domain in production)
      const url = new URL(redirectUrl, window.location.origin);
      navigate(url.pathname + url.search);
    } catch {
      // Fallback in case redirect_url is already just a path like "/products?..."
      navigate(redirectUrl);
    }
  };

  return (
    <div className="home">
      {/* ── Hero ──────────────────────────────────────── */}
      <section className="hero">
        {bannersLoading ? (
          <div className="hero__slide--skeleton" />
        ) : banners.length === 0 ? null : (
          <>
            {banners.map((banner, i) => (
              <picture
                key={banner.id}
                className={`hero__slide ${i === slide ? 'active' : ''} ${banner.redirect_url ? 'hero__slide--clickable' : ''}`}
                onClick={() => handleBannerClick(banner.redirect_url)}
                role={banner.redirect_url ? 'button' : undefined}
                tabIndex={banner.redirect_url ? 0 : undefined}
                aria-label={banner.redirect_url ? 'View banner details' : undefined}
                onKeyDown={(e) => {
                  if (banner.redirect_url && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleBannerClick(banner.redirect_url);
                  }
                }}
              >
                {banner.mobile_image_url && (
                  <source media="(max-width: 768px)" srcSet={banner.mobile_image_url} />
                )}
                <img src={banner.image_url} alt="" />
              </picture>
            ))}
            {banners.length > 1 && (
              <div className="hero__dots">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    className={`hero__dot ${i === slide ? 'active' : ''}`}
                    onClick={() => setSlide(i)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Trust bar ─────────────────────────────────── */}
      <div className="trust-bar">
        <div className="container">
          <div className="trust-bar__inner">
            {[
              { icon: '🚚', title: 'PAN India Shipping', sub: 'Anywhere, Anytime!' },
              { icon: '🎀', title: 'Gift Wrapping', sub: 'Complimentary on request' },
              { icon: '💳', title: 'Secure Payments', sub: 'UPI, Cards, Net Banking' },
              { icon: '💰', title: 'Unbeatable Prices', sub: 'Affordable and best prices' },
              { icon: '💎', title: 'Top-notch Quality', sub: 'Best quality products' },
            ].map((item) => (
              <div key={item.title} className="trust-item">
                <span className="trust-item__icon">{item.icon}</span>
                <div>
                  <p className="trust-item__title">{item.title}</p>
                  <p className="trust-item__sub">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Shop by Occasion (dynamic categories from API) ─ */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Shop by Occasion</p>
            <h2 className="section-title">Gifts for Every Moment</h2>
            <p className="section-subtitle">Find the perfect gift for every celebration, milestone, and meaningful moment.</p>
          </div>

          {catsLoading ? (
            <div className="categories-grid">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="category-card-skeleton">
                  <div className="skeleton-icon" />
                  <div style={{ flex: 1 }}>
                    <div className="skeleton-line" style={{ width: '70%', marginBottom: 6 }} />
                    <div className="skeleton-line" style={{ width: '40%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="categories-grid">
              {categoryTree.map((cat, i) => {
                const meta = CAT_ICONS[cat.slug] || defaultCat;
                return (
                  <Link
                    key={cat.id}
                    to={`/products?category=${cat.slug}`}
                    className="category-card"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className="category-card__icon" style={{ background: meta.color }}>
                      {meta.icon}
                    </div>
                    <div>
                      <p className="category-card__name">{cat.name}</p>
                    </div>
                    <svg className="category-card__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <InstagramReels instagramUrl="https://www.instagram.com/f_creation09" />

      {/* ── Bestsellers (is_featured === true) ────────── */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-header-row">
            <div>
              <p className="section-label">Customer Favourites</p>
              <h2 className="section-title">Bestsellers</h2>
            </div>
            <Link to="/products?filter=featured" className="btn btn-outline">View All →</Link>
          </div>

          {bsError ? (
            <SectionError />
          ) : bsLoading ? (
            <div className="products-grid">
              {[1, 2, 3, 4].map((n) => <ProductSkeleton key={n} />)}
            </div>
          ) : featuredProducts.length === 0 ? (
            <p className="section-empty">No bestsellers found.</p>
          ) : (
            <div className="products-grid">
              {featuredProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} delay={i * 60} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── New Arrivals (is_new_arrival === true) ─────── */}
      <section className="section">
        <div className="container">
          <div className="section-header-row">
            <div>
              <p className="section-label">On Demand</p>
              <h2 className="section-title">New Arrivals</h2>
            </div>
            <Link to="/products?filter=new" className="btn btn-outline">All Products →</Link>
          </div>

          {naError ? (
            <SectionError />
          ) : naLoading ? (
            <div className="products-grid">
              {[1, 2, 3, 4].map((n) => <ProductSkeleton key={n} />)}
            </div>
          ) : newProducts.length === 0 ? (
            <p className="section-empty">No new arrivals found.</p>
          ) : (
            <div className="products-grid">
              {newProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} delay={i * 80} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Trusted Clients ───────────────────────────── */}
      {(clientsLoading || clients.length > 0) && (
        <section className="section clients-section">
          <div className="container">
            <div className="section-header">
              <p className="section-label">In Good Company</p>
              <h2 className="section-title">Brands That Trust Us</h2>
              <p className="section-subtitle">
                From intimate celebrations to large-scale corporate gifting — these brands chose us.
              </p>
            </div>

            {clientsLoading ? (
              /* Skeleton */
              <div className="clients-track-wrapper">
                <div className="clients-track clients-track--static">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div key={n} className="client-logo-skeleton" />
                  ))}
                </div>
              </div>
            ) : (
              /* Infinite marquee — duplicate list for seamless loop */
              <div className="clients-track-wrapper">
                <div className="clients-track">
                  {[...clients, ...clients].map((client, i) => (
                    <div key={`${client.id}-${i}`} className="client-logo-card">
                      <img
                        src={client.imageUrl}
                        alt={client.name || 'Trusted client'}
                        // className="client-logo-img"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Testimonials ──────────────────────────────── */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-header">
            <p className="section-label">Happy Customers</p>
            <h2 className="section-title">What People Say</h2>
          </div>

          <div className="reviews-outer">
            <ReactGoogleReviews
              layout="custom"
              featurableId={featurableWidgetId}
              averageRating={3}
              renderer={(reviews) => <ReviewsCarousel reviews={reviews} />}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;